import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Landmark, Loader2, RotateCcw } from 'lucide-react';
import type { components } from '@/api/schema';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { Button } from '@/components/ui/button';
import { BankContractBadge } from './BankContractBadge';

type Contract = components['schemas']['ContractResource'];

/** T235: admin view of the bank registration — what the bank answered, and a retry for a failed or stuck one. */
export default function BankRegistrationPanel({ contract, locale }: { contract: Contract; locale: string }) {
  const { t } = useTranslation();
  const { hasRole } = useAdminAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  if (!contract.bank_status) return null;
  const canRetry = hasRole('platform_owner', 'finance_admin') && (contract.bank_status === 'failed' || contract.bank_status === 'in_progress');

  const retry = async () => {
    setBusy(true);
    try {
      const { error } = await apiClient.POST('/admin/contracts/{contract}/bank-retry', { params: { path: { contract: contract.id } } });
      if (error) throw error;
      toast.success(t('adm_contracts.bank_retry_done'));
      qc.invalidateQueries({ queryKey: ['/admin/contracts/{contract}'] });
      qc.invalidateQueries({ queryKey: ['/admin/contracts'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Landmark size={16} className="text-primary" /> {t('adm_contracts.bank_title')}
          <BankContractBadge status={contract.bank_status} labels="adm_contracts.bank_st" />
        </h2>
        {canRetry && (
          <Button size="sm" variant="outline" disabled={busy} onClick={retry}>
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <RotateCcw size={14} className="mr-1" />}
            {t('adm_contracts.bank_retry')}
          </Button>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-2 text-xs">
        <div><span className="text-muted-foreground">{t('adm_contracts.bank_contract_id')}:</span> <span className="font-mono text-foreground">{contract.bank_contract_id || '—'}</span></div>
        <div><span className="text-muted-foreground">{t('adm_contracts.bank_attempts')}:</span> <span className="text-foreground">{contract.bank_attempts ?? 0}</span></div>
        <div><span className="text-muted-foreground">{t('adm_contracts.bank_registered_at')}:</span> <span className="text-foreground">{contract.bank_registered_at ? new Date(contract.bank_registered_at).toLocaleString(locale) : '—'}</span></div>
      </div>
      {contract.bank_error && (
        <p className="text-xs text-destructive break-all"><span className="font-medium">{t('adm_contracts.bank_error')}:</span> {contract.bank_error}</p>
      )}
    </div>
  );
}
